/**
 * React hydration mismatches, recognised by message.
 *
 * The app deliberately survives these rather than showing an error: React
 * recovers by re-rendering on the client, so the page ends up correct. Every
 * error boundary has to agree on that, which is why the list lives here and not
 * inside one of them — a boundary that does not know these strings turns a
 * recovered mismatch into a visible failure.
 */

/**
 * Wordings React 18 uses for a mismatch, across its dev and production builds.
 * Also drives how much of the flood is sampled into Sentry.
 */
export const HYDRATION_ERROR_SIGNATURES = [
  'Text content does not match server-rendered HTML.',
  'Text content did not match.',
  'Hydration failed because the initial UI does not match what was rendered on the server.',
  // React 18 production wording; substring avoids the apostrophe in "didn't".
  'Hydration failed because the server rendered',
  'There was an error while hydrating.',
];

export const isHydrationErrorMessage = (message: string): boolean =>
  HYDRATION_ERROR_SIGNATURES.some((signature) => message.includes(signature));

/**
 * Looser prefixes for the boundaries themselves: an error that reaches a
 * boundary has been wrapped and reworded, so matching the full sentences above
 * would miss it.
 */
const TOLERATED_PREFIXES = [
  'Text content does not match',
  'Warning: Text content did not match',
  'Hydration failed',
  'There was an error while hydrating',
  'An error occurred during hydration',
  'Prop `placeholder` did not match',
];

/**
 * Whether an error boundary should let this one pass instead of rendering a
 * failure state.
 */
export const isToleratedHydrationError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : '';
  return Boolean(
    message && TOLERATED_PREFIXES.some((prefix) => message.includes(prefix)),
  );
};
